import { StyledList, StyledListItem, StyledPopover } from '@/components/AssetsPage/PopoverList';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { useFolderHierarchy } from '@/hooks/api/folders/useFolderHierarchy';
import { useOptimisticFolderName, getOptimisticFolderReference } from '@/hooks/useOptimisticFolderName';
import { usePopover } from '@/hooks/usePopover';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { useTenants } from '@/services/tenants/useTenants';
import { Folder as FolderIcon, Home, MoreHoriz } from '@mui/icons-material';
import {
  Breadcrumbs,
  Box,
  Button,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useSearchParams } from 'react-router-dom';
import { useRef } from 'react';
import { Folder } from '@/api/generatedApi';

type FolderBreadcrumb = Pick<Folder, 'id' | 'name' | 'parentFolderId'>;

const HiddenFoldersIconButton = styled(IconButton)(({ theme }) => ({
  width: '26px',
  height: '26px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  flexShrink: 0,
  '& svg': {
    fontSize: '18px',
  },
}));

const MAX_VISIBLE_FOLDERS = 4;

export const FolderBreadcrumbs = ({
  folderId,
  showRootFolder,
  onNavigate,
}: {
  folderId?: string;
  showRootFolder?: boolean;
  onNavigate?: (folderId?: string, folderName?: string) => void;
}) => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { setOptimisticFolder } = useOptimisticFolderName();

  const handleNavigate = (newFolderId?: string, folderName?: string) => {
    if (folderName && newFolderId) {
      setOptimisticFolder(newFolderId, folderName);
    }
    if (onNavigate) {
      onNavigate(newFolderId, folderName);
    } else {
      const newParameters = new URLSearchParams(searchParameters);
      if (newFolderId) {
        newParameters.set('folderId', newFolderId);
      } else {
        newParameters.delete('folderId');
      }
      setSearchParameters(newParameters);
    }
  };

  const {
    open: openHiddenFolders,
    close: closeHiddenFolders,
    isOpen: isHiddenFoldersOpen,
    anchorElement: hiddenFoldersAnchorElement,
  } = usePopover();

  const { getPath } = useTenants();
  const { hierarchy, isLoading } = useFolderHierarchy({ folderId });
  const { getOptimisticFolder, clearOptimisticFolder } = useOptimisticFolderName();

  const previousHierarchyReference = useRef<FolderBreadcrumb[]>([]);

  if (hierarchy?.length) {
    previousHierarchyReference.current = hierarchy.map(({ id, name, parentFolderId }) => ({
      id,
      name,
      parentFolderId,
    }));
    const optimisticFolder = getOptimisticFolder(folderId);
    if (folderId && optimisticFolder) {
      clearOptimisticFolder();
    }
  }

  const optimisticFolderName = folderId ? getOptimisticFolder(folderId) : undefined;
  const previousHierarchy = previousHierarchyReference.current;
  const hasPreviousHierarchy = previousHierarchy.length > 0;
  const folderNotInPrevious = folderId && !previousHierarchy.some(f => f.id === folderId);

  const createOptimisticFolder = (id: string, name: string, parentId?: string): FolderBreadcrumb => ({
    id,
    name,
    parentFolderId: parentId,
  });

  let optimisticHierarchy: FolderBreadcrumb[] = [];
  if (folderId && optimisticFolderName) {
    if (hasPreviousHierarchy && folderNotInPrevious) {
      optimisticHierarchy = [
        ...previousHierarchy,
        createOptimisticFolder(folderId, optimisticFolderName, previousHierarchy.at(-1)?.id),
      ];
    } else if (!hasPreviousHierarchy) {
      optimisticHierarchy = [createOptimisticFolder(folderId, optimisticFolderName)];
    }
  }

  const optimisticFolderReference = getOptimisticFolderReference();
  const hasOptimisticFolderPending = optimisticFolderReference.current !== undefined;
  const isNavigatingToFolder = !folderId && hasOptimisticFolderPending && previousHierarchy.length > 0;

  if (!folderId && !hasOptimisticFolderPending && previousHierarchy.length > 0) {
    previousHierarchyReference.current = [];
  }

  let displayHierarchy: FolderBreadcrumb[] = [];
  if (folderId) {
    if (hierarchy?.length) {
      displayHierarchy = hierarchy.map(({ id, name, parentFolderId }) => ({
        id,
        name,
        parentFolderId,
      }));
    } else if (optimisticHierarchy.length > 0) {
      displayHierarchy = optimisticHierarchy;
    } else if (previousHierarchy.length > 0) {
      displayHierarchy = previousHierarchy;
    }
  } else if (isNavigatingToFolder) {
    displayHierarchy = previousHierarchy;
  }

  const visibleFolders = displayHierarchy.slice(-MAX_VISIBLE_FOLDERS);
  const hiddenFolders = displayHierarchy.slice(0, -MAX_VISIBLE_FOLDERS);
  const hasHiddenFolders = hiddenFolders.length > 0;

  const hiddenPathTooltip = hiddenFolders.map(f => f.name).join(' / ');

  return (
    <>
      <StyledPopover
        open={isHiddenFoldersOpen}
        anchorEl={hiddenFoldersAnchorElement}
        onClose={closeHiddenFolders}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: '20px',
              marginTop: '0px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          {hasHiddenFolders && (
            <ListItemButton
              onClick={() => {
                handleNavigate();
                closeHiddenFolders();
              }}
              sx={{ pl: 2, py: 0.75 }}
            >
              <ListItemIcon sx={{ minWidth: 20, mr: 1 }}>
                <FolderIcon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <ListItemText>Assets</ListItemText>
            </ListItemButton>
          )}
          {hiddenFolders.map((folder, index) => {
            const indentLevel = index + 1;
            const indentSx = { pl: 2 + indentLevel * 2, py: 0.75 };
            const iconSx = { minWidth: 20, mr: 1 };
            const icon = (
              <ListItemIcon sx={iconSx}>
                <FolderIcon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
            );
            const text = <ListItemText>{folder.name}</ListItemText>;

            return showRootFolder || onNavigate ? (
              <ListItemButton
                key={folder.id}
                onClick={() => {
                  handleNavigate(folder.id, folder.name);
                  closeHiddenFolders();
                }}
                sx={indentSx}
              >
                {icon}
                {text}
              </ListItemButton>
            ) : (
              <Link
                key={folder.id}
                to={getPath(`assets?folderId=${folder.id}`)}
                style={{ textDecoration: 'none', color: 'inherit' }}
                onClick={closeHiddenFolders}
              >
                <StyledListItem sx={indentSx}>
                  {icon}
                  {text}
                </StyledListItem>
              </Link>
            );
          })}
        </StyledList>
      </StyledPopover>
      <Breadcrumbs
        aria-label='breadcrumb'
        data-testid={AssetsPageObjectModel.FolderBreadcrumbs}
        sx={{
          color: 'text.primary',
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.2s ease-in-out',
          flexWrap: 'nowrap',
          minWidth: 0,
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
          '& .MuiBreadcrumbs-li': {
            overflow: 'hidden',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 1,
          },
          '& .MuiBreadcrumbs-li:has([data-no-shrink])': {
            flexShrink: 0,
            minWidth: 'fit-content',
          },
          '& .MuiBreadcrumbs-li > [data-no-shrink]': {
            flexShrink: 0,
            minWidth: 'fit-content',
          },
        }}
      >
        {displayHierarchy.length > 0 && !hasHiddenFolders && (
          <Box data-no-shrink sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {showRootFolder || onNavigate ? (
              <Tooltip title='Assets' placement='bottom'>
                <IconButton
                  data-root-breadcrumb
                  onClick={() => handleNavigate()}
                  size='small'
                  sx={{ padding: '2px', color: 'inherit' }}
                >
                  <Home sx={{ fontSize: '20px' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title='Assets' placement='bottom'>
                <IconButton
                  data-root-breadcrumb
                  component={Link}
                  to={getPath('assets')}
                  size='small'
                  sx={{ padding: '2px', color: 'inherit' }}
                >
                  <Home sx={{ fontSize: '20px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        {hasHiddenFolders && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} data-hidden-folders-button data-no-shrink>
            <Tooltip title={hiddenPathTooltip} placement='bottom'>
              <HiddenFoldersIconButton onClick={openHiddenFolders}>
                <MoreHoriz />
              </HiddenFoldersIconButton>
            </Tooltip>
          </Box>
        )}
        {visibleFolders.map(folder =>
          showRootFolder || onNavigate ? (
            <Button
              key={folder.id}
              onClick={() => handleNavigate(folder.id, folder.name)}
              sx={{
                padding: 0,
                minWidth: 0,
                color: 'inherit',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: 'inherit',
                overflow: 'hidden',
                display: 'block',
                flexShrink: 1,
              }}
            >
              <BodyText sx={{ minWidth: 0 }}>
                <Ellipsis text={folder.name} tooltipProps={{ placement: 'bottom' }} />
              </BodyText>
            </Button>
          ) : (
            <Link
              key={folder.id}
              to={getPath(`assets?folderId=${folder.id}`)}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                overflow: 'hidden',
                display: 'block',
                flexShrink: 1,
              }}
            >
              <BodyText sx={{ minWidth: 0 }}>
                <Ellipsis text={folder.name} tooltipProps={{ placement: 'bottom' }} />
              </BodyText>
            </Link>
          )
        )}
        <span />
      </Breadcrumbs>
    </>
  );
};
